import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../components/ui/ToastModal';
import { useAuth } from './useAuth';
import { logError } from "../lib/logError";

export interface PnlCategory {
    name: string;
    is_profit_deduction: boolean;
    group: 'Wages' | 'Materials' | 'Other';
    /**
     * SEQ-182 "Include in Financial P&L". Undefined means true, so every
     * category saved before this field existed keeps deducting exactly as it
     * did. The rule itself, and the reason the name matches
     * pnl_metric_registry.financial_inclusion, live with the engine that
     * reads it: src/lib/pnl/pnlCalculationCore.ts.
     */
    financial_inclusion?: boolean;
}

export const DEFAULT_CATEGORIES: PnlCategory[] = [
    { name: "Core costs", is_profit_deduction: true, group: "Other" },
    { name: "Materials", is_profit_deduction: false, group: "Materials" }, // Materials tracked but ignored for actual net profit calculation based on 30% rule
    { name: "Engineers", is_profit_deduction: true, group: "Materials" },
    { name: "Handrails & Stairs", is_profit_deduction: true, group: "Materials" },
    { name: "Misc items", is_profit_deduction: true, group: "Materials" },
    { name: "Contractors", is_profit_deduction: true, group: "Wages" },
    { name: "Stripping of formwork", is_profit_deduction: true, group: "Wages" },
    { name: "Scaffold contractors", is_profit_deduction: true, group: "Wages" },
    { name: "Labour Hire", is_profit_deduction: true, group: "Wages" },
    { name: "Wages", is_profit_deduction: true, group: "Wages" },
    { name: "Patching", is_profit_deduction: true, group: "Other" },
    { name: "Extra wages", is_profit_deduction: true, group: "Wages" },
    { name: "Yard", is_profit_deduction: true, group: "Other" },
    { name: "Office", is_profit_deduction: true, group: "Other" },
    { name: "Misc Projects", is_profit_deduction: true, group: "Other" }
];

export function usePnlCategories() {
    const [categories, setCategories] = useState<PnlCategory[]>(DEFAULT_CATEGORIES);
    const [loading, setLoading] = useState(true);
    const { addToast } = useToast();
    const { organizationId } = useAuth();

    const fetchCategories = async () => {
        if (!organizationId) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('app_settings' as 'claims_projects')
                .select('pnl_expense_categories')
                .eq('organization_id', organizationId)
                .limit(1)
                .single() as { data: Record<string, unknown> | null; error: Error | null };

            if (error) throw error;

            if (data?.pnl_expense_categories && Array.isArray(data.pnl_expense_categories)) {
                const parsed = (data.pnl_expense_categories as unknown[]).map((c) => {
                    if (typeof c === 'string') {
                        // Migration logic for old string arrays
                        const isWages = ["Contractors", "Stripping of formwork", "Scaffold contractors", "Labour Hire", "Wages", "Extra wages"].includes(c);
                        const isMat = ["Materials", "Engineers", "Handrails & Stairs", "Misc items"].includes(c);
                        return {
                            name: c,
                            is_profit_deduction: c !== "Materials",
                            group: isWages ? "Wages" : (isMat ? "Materials" : "Other"),
                            // A string-array entry pre-dates the flag entirely, so it
                            // is a financial line by definition: the V1 engine is the
                            // only thing that has ever read it.
                            financial_inclusion: true
                        } as PnlCategory;
                    }
                    return c as PnlCategory;
                });
                setCategories(parsed);
            }
        } catch (error) {
            logError('Error fetching P&L categories:', error);
            // Fallback to defaults already set
        } finally {
            setLoading(false);
        }
    };

    const updateCategories = async (newCategories: PnlCategory[]) => {
        if (!organizationId) {
            addToast({ type: 'error', title: 'Update Failed', message: 'No organization in context.' });
            return false;
        }
        try {
            const { data: currentSettings, error: fetchError } = await supabase
                .from('app_settings' as 'claims_projects')
                .select('id')
                .eq('organization_id', organizationId)
                .limit(1)
                .single() as { data: Record<string, unknown> | null; error: Error | null };

            if (fetchError) throw fetchError;

            const { error: updateError } = await supabase
                .from('app_settings' as 'claims_projects')
                .update({ pnl_expense_categories: newCategories } as Record<string, unknown>)
                .eq('id', (currentSettings as Record<string, unknown>).id as string)
                .eq('organization_id', organizationId) as { error: Error | null };

            if (updateError) throw updateError;

            setCategories(newCategories);
            addToast({ type: 'success', title: 'Categories Updated', message: 'Category list saved successfully.' });
            return true;
        } catch (error) {
            logError('Error updating P&L categories:', error);
            addToast({ type: 'error', title: 'Update Failed', message: 'Could not save categories.' });
            return false;
        }
    };

    useEffect(() => {
        fetchCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCategories is redefined each render; organizationId is the only input that should re-trigger the fetch
    }, [organizationId]);

    return { categories, loading, updateCategories, refreshCategories: fetchCategories };
}
